import {
  type CreateLostReasonRequest,
  type LostReasonDetail,
  LostReasonDetailSchema,
  type LostReasonListQuery,
  type LostReasonListResponse,
  LostReasonListResponseSchema,
  type UpdateLostReasonRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Lost Reason master API. Every response is validated with the shared Zod
 * schema so a server change can't sneak past the UI types.
 */

export const listLostReasons = async (
  query: LostReasonListQuery,
): Promise<LostReasonListResponse> => {
  const { data } = await apiClient.get<unknown>('/lost-reasons', { params: query });
  return LostReasonListResponseSchema.parse(data);
};

export const getLostReason = async (id: string): Promise<LostReasonDetail> => {
  const { data } = await apiClient.get<unknown>(`/lost-reasons/${id}`);
  return LostReasonDetailSchema.parse(data);
};

export const createLostReason = async (
  payload: CreateLostReasonRequest,
): Promise<LostReasonDetail> => {
  const { data } = await apiClient.post<unknown>('/lost-reasons', payload);
  return LostReasonDetailSchema.parse(data);
};

export const updateLostReason = async (
  id: string,
  payload: UpdateLostReasonRequest,
): Promise<LostReasonDetail> => {
  const { data } = await apiClient.patch<unknown>(`/lost-reasons/${id}`, payload);
  return LostReasonDetailSchema.parse(data);
};

export const deleteLostReason = async (id: string): Promise<void> => {
  await apiClient.delete(`/lost-reasons/${id}`);
};

export const bulkDeleteLostReasons = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/lost-reasons/bulk-delete', { ids });
  return data;
};

export const bulkSetLostReasonsStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/lost-reasons/bulk-set-status', { ids, isActive });
  return data;
};
